import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Primes a list-query family for a route loader, with cold-vs-warm behaviour:
 *
 * - **Cold** (nothing cached yet for `listKeyPrefix`): the loader awaits the
 *   fetch (returns the promise) so first navigation into the route is
 *   flash-free — the route's `pendingComponent` covers the wait.
 * - **Warm** (any page/filter/sort combo of this list has been fetched
 *   before): the loader fires the priming call and returns immediately. A
 *   first-time filter/sort/page combination on an already-visited route must
 *   NOT re-trigger the route's full-page `pendingComponent` — the page's own
 *   `useQuery` + `isFetching` + confined `TableRefetchOverlay` owns that
 *   transition instead (see `docs/plans/frontend-loader-state-skeletons.md`).
 *
 * Swallows fetch errors either way — a loader failure must never
 * short-circuit the route render; the page owns its own loading/error/empty
 * states.
 */
export function primeListQuery<T>(
  queryClient: QueryClient,
  listKeyPrefix: QueryKey,
  queryOptions: { queryKey: QueryKey; queryFn: () => Promise<T> },
): Promise<T | undefined> | undefined {
  const hasCachedList =
    queryClient.getQueryCache().findAll({ queryKey: listKeyPrefix }).length > 0;
  const prime = () => queryClient.ensureQueryData(queryOptions).catch(() => undefined);
  if (hasCachedList) {
    void prime();
    return undefined;
  }
  return prime();
}
